import { isStandalone } from 'flavours/glitch/features/standalone/auth';
import { getBackendLinks } from 'flavours/glitch/features/standalone/backend_links';
import { domain } from 'flavours/glitch/initial_state';

// Standalone mode (the static build logged into a remote instance) needs
// backend links to point at the user's instance — or to be hidden when the
// target software does not expose them. In regular (Rails-served) mode these
// keep the upstream relative paths.
const standalone = isStandalone();

const instanceUrl = (path: string): string =>
  standalone && domain ? `https://${domain}${path}` : path;

// Software-aware links resolve asynchronously (NodeInfo detection). Until then,
// getters fall back to a best-effort value.
let backendLinks: Awaited<ReturnType<typeof getBackendLinks>> | null = null;
void getBackendLinks()
  .then((links) => {
    backendLinks = links;
  })
  .catch(() => {
    // Ignore detection errors; getters use their fallbacks.
  });

export const getPreferencesLink = (): string =>
  backendLinks?.preferencesLink ?? instanceUrl('/settings/preferences');
export const getProfileLink = (): string =>
  backendLinks?.profileLink ?? instanceUrl('/settings/profile');
export const getSignOutLink = (): string =>
  backendLinks?.signOutLink ?? (standalone ? '/logout' : '/auth/sign_out');
export const getPrivacyPolicyLink = (): string =>
  backendLinks?.privacyPolicyLink ??
  (standalone ? instanceUrl('/about') : '/privacy-policy');
export const getAccountAdminLink = (id: string): string | undefined =>
  backendLinks?.accountAdminLink?.(id) ??
  (standalone ? undefined : `/admin/accounts/${id}`);
export const getStatusAdminLink = (
  account_id: string,
  status_id: string,
): string | undefined =>
  backendLinks?.statusAdminLink?.(account_id, status_id) ??
  (standalone
    ? undefined
    : `/admin/accounts/${account_id}/statuses/${status_id}`);
export const getFilterEditLink = (id: string): string | undefined =>
  backendLinks?.filterEditLink?.(id) ??
  (standalone ? undefined : `/filters/${id}/edit`);
export const getFiltersLink = (): string | undefined =>
  backendLinks?.filtersLink ?? (standalone ? undefined : '/filters');
export const getMutesLink = (): string | undefined =>
  backendLinks?.mutesLink ?? (standalone ? undefined : '/mutes');
export const getBlocksLink = (): string | undefined =>
  backendLinks?.blocksLink ?? (standalone ? undefined : '/blocks');
export const getDomainBlocksLink = (): string | undefined =>
  backendLinks?.domainBlocksLink ?? (standalone ? undefined : '/domain_blocks');
export const getPrivacyLink = (): string | undefined =>
  backendLinks?.privacyLink ??
  (standalone ? instanceUrl('/settings/privacy') : '/settings/privacy');
export const getStatusesCleanupLink = (): string | undefined =>
  backendLinks?.statusesCleanupLink ??
  (standalone ? undefined : '/statuses_cleanup');
export const getExportLink = (): string | undefined =>
  backendLinks?.exportLink ??
  (standalone ? instanceUrl('/settings/export') : '/settings/export');
export const getAdminReportsLink = (): string | undefined =>
  backendLinks?.adminReportsLink ?? (standalone ? undefined : '/admin/reports');
export const getAdminDashboardLink = (): string | undefined =>
  backendLinks?.adminDashboardLink ??
  (standalone ? undefined : '/admin/dashboard');
export const getRelationshipsLink = (): string =>
  backendLinks?.relationshipsLink ?? instanceUrl('/relationships');
export const getSecurityLink = (): string | undefined =>
  backendLinks?.securityLink ?? (standalone ? undefined : '/auth/edit');

// Const exports kept for the existing importers (status action bars, local
// settings). Standalone-aware, with simple (non-software-detected) fallbacks.
export const preferencesLink = instanceUrl('/settings/preferences');
export const profileLink = instanceUrl('/settings/profile');
export const signOutLink = standalone ? '/logout' : '/auth/sign_out';
export const privacyPolicyLink = standalone
  ? instanceUrl('/about')
  : '/privacy-policy';
export const accountAdminLink = (id: string): string | undefined =>
  standalone ? undefined : `/admin/accounts/${id}`;
export const statusAdminLink = (
  account_id: string,
  status_id: string,
): string | undefined =>
  standalone
    ? undefined
    : `/admin/accounts/${account_id}/statuses/${status_id}`;
export const filterEditLink = (id: string): string | undefined =>
  standalone ? undefined : `/filters/${id}/edit`;
export const relationshipsLink = instanceUrl('/relationships');
export const securityLink = standalone ? undefined : '/auth/edit';

export const preferenceLink = (setting_name: string): string | undefined => {
  if (standalone) {
    return instanceUrl('/settings/preferences');
  }
  switch (setting_name) {
    case 'user_setting_expand_spoilers':
    case 'user_setting_disable_swiping':
      return `/settings/preferences/appearance#${setting_name}`;
    default:
      return preferencesLink;
  }
};
